import {AutomaticInvoicingOccurrence, BillingCycleFilter, SessionType} from 'web-app/util/graphql/api-types';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import type * as ApiTypes from '../types';
import type * as LocalTypes from './types';

export const transformApiChildData = (
    child: ApiTypes.FinanceOverviewVersioned['children'][number]['child'],
): LocalTypes.ChildData => ({
    id: child.id,
    birthday: child.birthday ?? null,
    startDate: child.startDate,
    name: {
        firstName: child.name.firstName,
        middleName: child.name.middleName ?? null,
        lastName: child.name.lastName ?? null,
        fullName: child.name.fullName,
    },
    profileImage: child.profileImage ? {url: child.profileImage.url} : null,
    group: child.group ? {title: child.group.title} : null,
    lastDay: child.endDate ?? null,
});

type ApiBillPayer = Omit<ApiTypes.FinanceOverviewVersioned['children'][number]['billPayers'][number], '__typename'>;

export const transformApiBillPayer = (billPayer: ApiBillPayer): LocalTypes.BillPayer => ({
    share: billPayer.share,
    hasAutomaticPaymentMethod: hasValue(billPayer.automaticPaymentMethodId),
    onAutomaticBilling: billPayer.onAutomaticBilling,
    nextInvoiceSum: billPayer.nextInvoice,
    balance: billPayer.balance,
    billingFrequency: billPayer.billingFrequency ?? null,
    billPayer: {
        billPayerId: billPayer.billPayer.billPayerId,
        name: billPayer.billPayer.name,
        email: billPayer.billPayer.email ?? null,
        phone: billPayer.billPayer.phone ?? null,
    },
    paymentMethods: billPayer.paymentMethods.map(paymentMethod => ({
        type: paymentMethod.type,
        active: paymentMethod.active,
        title: paymentMethod.title,
        isUsedForAutopay: paymentMethod.id === billPayer.automaticPaymentMethodId,
    })),
    latestInvoice: billPayer.latestInvoice
        ? {
              invoiceDate: billPayer.latestInvoice.invoiceDate,
              total: billPayer.latestInvoice.total,
              invoiceId: billPayer.latestInvoice.invoiceId ?? null,
          }
        : null,
});

const transformApiFundingTime = (
    funding: ApiTypes.FinanceOverviewVersioned['children'][number]['plans'][number]['parts'][number]['funding'],
): LocalTypes.FundingTime | null => {
    if (funding?.time.__typename === 'PlanFundingTimeHours') {
        return {type: 'planFundingTimeHours', hours: funding?.time.hours};
    }

    if (funding?.time.__typename === 'PlanFundingTimeMinutes') {
        return {type: 'planFundingTimeMinutes', minutes: funding?.time.minutes};
    }

    return null;
};

const transformApiFunding = (
    funding: ApiTypes.FinanceOverviewVersioned['children'][number]['plans'][number]['parts'][number]['funding'],
): LocalTypes.Funding | null => {
    if (funding?.__typename === 'PlanFundingAssisted') {
        return {type: 'planFundingAssisted', method: funding.method, time: transformApiFundingTime(funding)};
    }

    if (funding?.__typename === 'PlanFundingManualByRate') {
        return {
            type: 'planFundingManualByRate',
            method: funding.method,
            time: transformApiFundingTime(funding),
            rate: funding.rate,
        };
    }

    if (funding?.__typename === 'PlanFundingManual') {
        return {
            type: 'planFundingManual',
            method: funding.method,
            time: transformApiFundingTime(funding),
            amount: funding.amount,
        };
    }
    return null;
};

const transformApiBookings = (
    bookings: ApiTypes.FinanceOverviewVersioned['children'][number]['plans'][number]['parts'][number]['bookings'],
): LocalTypes.Part['bookings'] => {
    return bookings.reduce<LocalTypes.Part['bookings']>(
        (acc, booking) => {
            if (booking.__typename === 'PlanProductBooking') {
                const product: LocalTypes.Product = {
                    type: 'product',
                    planId: booking.planId,
                    planPartId: booking.planPartId,
                    productAvailability: {
                        product: {
                            title: booking.productAvailability.product.title,
                            id: booking.productAvailability.product.productId,
                        },
                    },
                    pricingGroups: booking.productAvailability.product.pricing.map(pricing => ({
                        price: pricing.price ?? '0',
                        pricingGroupId: pricing.pricingGroupId,
                    })),
                };
                acc.products.push(product);
                acc.all.push(product);
            }

            if (booking.__typename === 'PlanSessionBooking') {
                const baseSession: LocalTypes.BaseSession = {
                    planId: booking.planId,
                    planPartId: booking.planPartId,
                    sessionAvailability: {
                        day: booking.sessionAvailability.day,
                        session: {
                            id: booking.sessionAvailability.session.sessionId,
                            title: booking.sessionAvailability.session.title,
                            type: booking.sessionAvailability.session.type,
                            pricing: booking.sessionAvailability.session.pricing.flatMap(p => {
                                if (!p.price) {
                                    return [];
                                }
                                return [
                                    {
                                        price: p.price,
                                        pricingGroupId: p.pricingGroupId,
                                    },
                                ];
                            }),
                        },
                    },
                    timeRange: {
                        from: booking.timeRange.from,
                        to: booking.timeRange.to,
                    },
                };

                if (booking.sessionAvailability.session.type === SessionType.PackageSession) {
                    const packageBooking: LocalTypes.Package = {...baseSession, type: 'package'};
                    acc.packages.push(packageBooking);
                    acc.all.push(packageBooking);
                } else {
                    const sessionBooking: LocalTypes.Session = {...baseSession, type: 'session'};
                    acc.sessions.push(sessionBooking);
                    acc.all.push(sessionBooking);
                }
            }
            return acc;
        },
        {products: [], sessions: [], packages: [], all: []},
    );
};

const transformApiPriceModifiers = (
    priceModifiers: ApiTypes.FinanceOverviewVersioned['children'][number]['plans'][number]['parts'][number]['priceModifiers'],
): LocalTypes.PriceModifier[] => {
    return priceModifiers.flatMap(priceModifier => {
        if (priceModifier.__typename === 'PlanPriceModifierDiscount') {
            return {
                type: 'discount',
                discount: {
                    title: priceModifier.title,
                    id: priceModifier.discountPresetId ?? null,
                    amount: priceModifier.amount,
                    percent: priceModifier.percent,
                    order: priceModifier.order,
                    period: priceModifier.period,
                },
            };
        }

        if (priceModifier.__typename === 'PlanPriceModifierSurcharge') {
            return {type: 'surcharge', surcharge: {title: priceModifier.title}};
        }

        return [];
    });
};

const transformApiPlanPart = (
    part: ApiTypes.FinanceOverviewVersioned['children'][number]['plans'][number]['parts'][number],
): LocalTypes.Part => {
    const priceModifiers = transformApiPriceModifiers(part.priceModifiers);
    const discounts = priceModifiers
        .filter(priceModifier => priceModifier.type === 'discount')
        .map(priceModifier => priceModifier.discount);
    const surcharges = priceModifiers
        .filter(priceModifier => priceModifier.type === 'surcharge')
        .map(priceModifier => priceModifier.surcharge);

    return {
        planPartId: part.planPartId,
        order: part.order,
        termTimeOnly: part.termTimeOnly,
        statistics: part.statistics,
        billingScheme: part.billingScheme,
        funding: transformApiFunding(part.funding),
        bookings: transformApiBookings(part.bookings),
        discounts,
        surcharges,
        attendanceSchedule: part.attendanceSchedule
            ? {
                  id: part.attendanceSchedule.id,
                  title: part.attendanceSchedule.title,
                  isFullYear: part.attendanceSchedule.fullYear ?? false,
              }
            : null,
    };
};

type ApiPlan = Omit<ApiTypes.FinanceOverviewVersioned['children'][number]['plans'][number], '__typename'>;

export const transformApiPlan = (plan: ApiPlan): LocalTypes.Plan => ({
    planId: plan.planId,
    range: {
        from: plan.range.from,
        to: plan.range.to ?? null,
    },
    pricingGroup: plan.pricingGroup
        ? {title: plan.pricingGroup.title, pricingGroupId: plan.pricingGroup.pricingGroupId}
        : null,
    ruleGroupId: plan.ruleGroupId ?? null,
    parts: plan.parts.map(transformApiPlanPart),
});

export const transformApiChild = (child: ApiTypes.FinanceOverviewVersioned['children'][number]): LocalTypes.Child => ({
    child: transformApiChildData(child.child),
    billPayers: child.billPayers.map(transformApiBillPayer),
    plans: child.plans.map(transformApiPlan),
    // the next invoice is now added programmatically once the next invoices are fetched separately
    nextInvoice: null,
});

const transformApiPaymentsAccountStatus = (
    paymentsAccountStatus: ApiTypes.FinanceOverviewVersioned['paymentsAccountStatus'],
): LocalTypes.PaymentsAccountStatus => {
    if (paymentsAccountStatus.__typename === 'FinanceOverviewPaymentsAccountStatusCanManageAccountExistsV2') {
        return {
            status: paymentsAccountStatus.status,
            type: 'CAN_MANAGE_ACCOUNT_EXISTS',
        };
    }

    if (paymentsAccountStatus.__typename === 'FinanceOverviewPaymentsAccountStatusCanManageAccountDoesntExistV2') {
        return {
            type: 'CAN_MANAGE_ACCOUNT_DOESNT_EXIST',
        };
    }

    return {
        type: 'CANNOT_MANAGE',
    };
};

const transformApiFiltersContext = (
    apiFiltersContext: ApiTypes.FinanceOverviewVersioned['filtersContext'],
): LocalTypes.FiltersContext => {
    const sessionItems = apiFiltersContext.sessions.map<LocalTypes.SessionOrProductItem>(session => ({
        title: session.title,
        id: session.id,
        type: session.type === SessionType.PackageSession ? 'package' : 'session',
    }));

    const productItems = apiFiltersContext.products.map<LocalTypes.SessionOrProductItem>(product => ({
        title: product.title,
        id: product.id,
        type: 'product',
    }));

    return {sessionOrProductItems: [...sessionItems, ...productItems]};
};

export const transformLocalSessionOrProductItemsToApiItems = (
    sessionOrProductItemsComingFromFilters: string[] | undefined,
    sessionOrProductItems: LocalTypes.SessionOrProductItem[],
) => {
    return sessionOrProductItemsComingFromFilters?.reduce<{sessionIds: string[]; productIds: string[]}>(
        (acc, currentVal) => {
            const sessionItem = sessionOrProductItems.find(
                item => item.id === currentVal && (item.type === 'package' || item.type === 'session'),
            );

            const productItem = sessionOrProductItems.find(item => item.id === currentVal && item.type === 'product');

            if (hasValue(sessionItem)) {
                acc.sessionIds.push(currentVal);
            }

            if (hasValue(productItem)) {
                acc.productIds.push(currentVal);
            }

            return acc;
        },
        {
            sessionIds: [],
            productIds: [],
        },
    );
};

const transformApiWidgetsToLocalWidgets = (
    widgets: ApiTypes.FinanceOverviewVersioned['widgets'],
): LocalTypes.Widgets => ({
    needsYourAttention: {
        missingBillPayerCount: widgets.needsYourAttention.missingBillPayerCount,
        missingBillPayerEmailCount: widgets.needsYourAttention.missingBillPayerEmailCount,
        missingPaymentMethodCount: widgets.needsYourAttention.missingPaymentMethodCount,
        missingPlanCount: widgets.needsYourAttention.missingPlanCount,
        missingChildBirthdayCount: widgets.needsYourAttention.missingChildBirthdayCount,
        purchasesPendingApprovalCount: widgets.needsYourAttention.purchasesPendingApprovalCount,
    },
});

export const transformApiFinanceOverview = (data: ApiTypes.FinanceOverviewVersioned): LocalTypes.FinanceOverview => ({
    cursor: data.cursor ?? null,
    isEnabled: true,
    shouldUseNewPowerPage: true,
    paymentsAccountStatus: transformApiPaymentsAccountStatus(data.paymentsAccountStatus),
    children: [...data.children].map(child => transformApiChild(child)),
    filtersContext: transformApiFiltersContext(data.filtersContext),
    widgets: transformApiWidgetsToLocalWidgets(data.widgets),
});

export const transformInvoiceFrequencyToBillingCycle = (invoiceFrequency: AutomaticInvoicingOccurrence | undefined) => {
    switch (invoiceFrequency) {
        case AutomaticInvoicingOccurrence.WEEKLY:
            return BillingCycleFilter.NextWeekly;
        case AutomaticInvoicingOccurrence.BIWEEKLY:
            return BillingCycleFilter.NextBiWeekly;
        case AutomaticInvoicingOccurrence.MONTHLY:
            return BillingCycleFilter.NextMonthly;

        default:
            return undefined;
    }
};
